/**
 * The link to a view, for people who have no account here.
 *
 * Offered only for view types this build has a public page for -- today, a
 * form. The backend would share any of the seven; six of them would produce a
 * link to a page that does not exist yet, and a link nobody can open is not a
 * link.
 *
 * **A new address is not asked about first.** It breaks every link already
 * handed out, which sounds like something to confirm -- but rotating is an
 * undoable action on the server, in the view's own scope, so the answer to "are
 * you sure" is already on the keyboard. The consequence is written beside the
 * button instead, which is the thing a confirmation would have said.
 */
import { ExternalLink, RefreshCw, Share2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { View } from '@/api/types'
import { CopyButton } from '@/shared/components/copy-button'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Switch } from '@/shared/ui/switch'
import { publicViewUrl } from './share'
import { useViewSharing } from './share-mutations'

export function ShareMenu({ tableId, view }: { tableId: number; view: View }) {
  const { t } = useTranslation()
  const { setShared, rotate, isPending } = useViewSharing(tableId, view.id)
  const url = publicViewUrl(view)

  if (url === null) return null
  const shared = view.public === true

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Share2 className="size-4 opacity-70" />
          {/* Whether it is shared, not just that sharing exists. A view anybody
              on the internet can read is not something to have to open a menu
              to find out. */}
          {shared ? t('share.shared') : t('share.share')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-3">
        <div className="flex items-center gap-2">
          <Switch
            id={`share-${view.id}`}
            size="sm"
            checked={shared}
            onCheckedChange={setShared}
          />
          <label htmlFor={`share-${view.id}`} className="text-sm font-medium">
            {t('share.toggle')}
          </label>
        </div>

        {!shared ? (
          <p className="mt-2 text-sm text-muted-foreground">{t('share.notShared')}</p>
        ) : (
          <>
            <div className="mt-3 flex items-center gap-2">
              {/* Readable and selectable rather than a line of text with a
                  button beside it: the clipboard needs a permission a browser
                  may refuse, and a box somebody can select by hand is the
                  fallback that always works. */}
              <Input readOnly value={url} aria-label={t('share.link')} className="h-8" />
              <CopyButton value={url} label={t('share.copy')} />
              <Button variant="outline" size="icon" className="size-8" asChild>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('share.open')}
                >
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
              <p className="text-xs text-muted-foreground">{t('share.rotateWhat')}</p>
              <Button variant="outline" size="sm" disabled={isPending} onClick={rotate}>
                <RefreshCw className="size-4" />
                {t('share.rotate')}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
